import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import { 
  ArrowLeft, 
  Hash, 
  Mail, 
  User, 
  Wallet, 
  CreditCard, 
  Banknote, 
  Building2, 
  FileText, 
  MessageCircle, 
  Clock, 
  CheckCircle, 
  BarChart3, 
  AlertTriangle,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

// USD kategorileri (USD/TRY dönüşümü gereken)
const USD_CATEGORIES = ['KRIPTO', 'EMTIA', 'ABD_NASDAQ', 'US_STOCKS', 'FOREX', 'ENDEKS', 'ENDEKSLER'];

// TL kategorileri - bu kategoriler dışındakiler USD
const TL_CATEGORIES = ['BIST', 'BIST 100', 'BIST 30', 'BIST_100', 'BIST_30', 'BIST YILDIZ', 'BIST ANA', 'BORSA', 'HISSE', 'VIOP', 'DOVIZ', 'FOREX'];

// Varlık USD mi TRY mi?
const isUsdAsset = (position) => {
  const category = position.category || '';
  const currency = position.currency || '';
  if (currency === 'USD') return true;
  if (currency === 'TRY') return false;
  return !TL_CATEGORIES.includes(category);
};

function ProfilePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [depositRequests, setDepositRequests] = useState([]);
  const [usdTryRate, setUsdTryRate] = useState(42.84); // USD/TRY kuru
  const [uploading, setUploading] = useState(false);
  const [positions, setPositions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [closedPnL, setClosedPnL] = useState(0);
  const [uploadMessage, setUploadMessage] = useState('');
  const [showMarginCallModal, setShowMarginCallModal] = useState(false);
  // İŞ B — Tek kaynak "Toplam Varlık" ve "İşlem Gücü" — backend'den
  const [accountSummary, setAccountSummary] = useState(null);
  
  // Faz 2 Paket 1.3: Backend'in profit_loss alanına güven (motor hesaplı).
  // Frontend YENIDEN hesaplama yapmaz — S1/S5 çözümü.
  const openPnL = positions.reduce((sum, pos) => sum + (Number(pos.profit_loss) || 0), 0);
  const totalPnL = openPnL + closedPnL;

  // Toplam varlık = Bakiye + Toplam K/Z
  const totalEquity = (user?.balance || 0) + openPnL;

  // Calculate trading statistics — Faz 2 Paket 1.3
  // Kural: usedMargin = Σ pos.teminat (grup ≠ hisse). Frontend hesaplama YOK.
  //         equity = balance + Σ profit_loss (grup ≠ hisse). Credit dahil edilmez.
  //         SPOT_CATEGORIES / hidden_leverage / leverage YOK — grup alanı backend'den gelir.
  const calculateTradingStats = () => {
    const balance = user?.balance || 0;

    let usedMargin = 0;
    let marginPnL = 0;
    let hasMarginPositions = false;

    positions.forEach((pos) => {
      const grup = String(pos.grup || '').toLowerCase();
      const isHisse = grup === 'hisse';

      if (!isHisse) {
        usedMargin += Number(pos.teminat) || 0;
        marginPnL += Number(pos.profit_loss) || 0;
        hasMarginPositions = true;
      }
    });

    // Ek metrik (K1 kararı): Portföy Değeri = bilgi amaçlı, margin hesabına girmez.
    // Hisse pozisyonlarının güncel değerini balance + credit ile birlikte gösterir.
    const spotValue = positions.reduce((acc, pos) => {
      const grup = String(pos.grup || '').toLowerCase();
      if (grup !== 'hisse') return acc;
      const adet = Math.abs(Number(pos.adet ?? pos.amount) || 0);
      const price = Number(pos.current_price ?? pos.live_price ?? pos.avg_buy_price) || 0;
      const kur = Number(pos.kur_acilis) || 1;
      return acc + adet * price * kur;
    }, 0);

    // equity, marginLevel: yalnız margin (hisse hariç) tarafında
    const equity = balance + marginPnL;
    const freeMargin = Math.max(0, equity - usedMargin);
    const marginLevel = usedMargin > 0 ? (equity / usedMargin) * 100 : 100;

    return {
      balance,
      credit: user?.credit || 0,
      equity,
      freeMargin,
      usedMargin,
      marginLevel,
      hasMarginPositions,
      spotValue,           // Bilgi amaçlı (K1)
      portfolioValue: balance + (user?.credit || 0) + spotValue, // Bilgi amaçlı
    };
  };

  // useMemo ile positions ve user değiştiğinde yeniden hesapla
  const tradingStats = useMemo(() => calculateTradingStats(), [positions, user, openPnL, usdTryRate]);
  
  // Margin Call — Faz 2 Paket 1.3: frontend bağımsız eşik KALDIRILDI.
  // Backend margin_call bildirimi (Ek-2/unified pipeline) ayrı bir kanaldan gelecek.
  useEffect(() => {
    if (!user || !user.id) return;
    // no-op — backend'in margin_call action'ı henüz frontend'e API ile taşınmadı.
  }, [tradingStats.marginLevel, tradingStats.usedMargin, tradingStats.equity, tradingStats.hasMarginPositions, user]);

  // Calculate statistics
  const stats = {
    totalTrades: transactions.length,
    openPositions: positions.length,
    totalPnL: totalPnL
  };

  // Pozisyon fiyatlarını anlık güncelle - BATCH optimizasyonu
  const updatePositionPrices = async () => {
    if (positions.length === 0) return;
    
    const token = localStorage.getItem('token');
    const config = { headers: { 'Authorization': `Bearer ${token}` } };
    
    try {
      // TEK API ÇAĞRISI ile tüm fiyatları al
      const symbols = [...new Set(positions.map(pos => pos.asset_symbol || pos.symbol))];
      const batchRes = await axios.post(`${API}/market/quotes/batch`, { symbols }, config);
      const priceMap = batchRes.data.quotes || {};
      
      const updatedPositions = positions.map(pos => {
        const symbol = pos.asset_symbol || pos.symbol;
        const priceData = priceMap[symbol];
        if (priceData) {
          return { 
            ...pos, 
            current_price: priceData.price || priceData.current_price,
            live_price: priceData.price || priceData.current_price 
          };
        }
        return pos;
      });
      setPositions(updatedPositions);
    } catch (err) {
      console.error('Price update error:', err);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  // İŞ B — Account summary fetch (Toplam Varlık + İşlem Gücü, tek kaynak)
  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const r = await axios.get(`${API}/account-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setAccountSummary(r.data);
      } catch (err) { /* sessizce geç */ }
    };
    fetchSummary();
    const iv = setInterval(fetchSummary, 8000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Pozisyon fiyatlarını her 5 saniyede güncelle (admin panel ile senkron)
  useEffect(() => {
    if (positions.length === 0) return;
    
    const priceInterval = setInterval(() => {
      updatePositionPrices();
    }, 5000); // 5 saniye - admin panel ile aynı
    
    return () => clearInterval(priceInterval);
  }, [positions.length]);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const config = {
        headers: { 'Authorization': `Bearer ${token}` }
      };

      // TÜM API ÇAĞRILARINI PARALEL YAP - 10-15 saniye yerine 1-2 saniye
      console.log('🚀 Starting parallel API calls...');
      const startTime = Date.now();
      
      const [userRes, depositsRes, positionsRes, transactionsRes, assetsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, config).catch(err => ({ data: null, error: err })),
        axios.get(`${API}/deposits/history`, config).catch(err => ({ data: [], error: err })),
        axios.get(`${API}/positions`, config).catch(err => ({ data: [], error: err })),
        axios.get(`${API}/transactions`, config).catch(err => ({ data: [], error: err })),
        axios.get(`${API}/assets/minimal`, config).catch(err => ({ data: { assets: [], usd_try_rate: 42.84 }, error: err }))
      ]);
      
      console.log(`✅ Parallel fetch completed in ${Date.now() - startTime}ms`);

      // USD/TRY kurunu al
      const rate = assetsRes.data?.usd_try_rate || 42.84;
      setUsdTryRate(rate);
      console.log('💱 USD/TRY Rate:', rate);
      
      // Asset map oluştur (kategori bilgisi için)
      const assetsList = assetsRes.data?.assets || assetsRes.data || [];
      const assetMap = {};
      assetsList.forEach(a => {
        assetMap[a.symbol] = { category: a.category, currency: a.currency };
      });

      // User data
      if (userRes.data && !userRes.error) {
        setUser(userRes.data);
      }

      // Deposits
      const deposits = Array.isArray(depositsRes.data) ? depositsRes.data : (depositsRes.data?.deposits || []);
      setDepositRequests(deposits);

      // Transactions
      const txList = Array.isArray(transactionsRes.data) 
        ? transactionsRes.data 
        : (transactionsRes.data?.transactions || []);
      setTransactions(txList);
      
      // Calculate closed P&L from transactions
      const closeTxs = txList.filter(tx => tx.transaction_type === 'CLOSE');
      let totalClosedPnL = 0;
      closeTxs.forEach(tx => {
        if (tx.profit_loss !== null && tx.profit_loss !== undefined) {
          totalClosedPnL += tx.profit_loss;
        } else if (tx.pnl !== null && tx.pnl !== undefined) {
          totalClosedPnL += tx.pnl;
        }
      });
      setClosedPnL(totalClosedPnL);

      // Positions - with batch price fetch
      const positionsData = Array.isArray(positionsRes.data) 
        ? positionsRes.data 
        : (positionsRes.data?.positions || []);
      console.log('🔍 Positions fetched:', positionsData.length, 'positions');
      
      if (positionsData.length === 0) {
        setPositions([]);
      } else {
        // TEK API ÇAĞRISI ile tüm fiyatları al (N API çağrısı yerine 1 çağrı)
        const symbols = [...new Set(positionsData.map(pos => pos.asset_symbol || pos.symbol))];
        console.log('📊 Fetching prices for:', symbols.join(', '));
        
        let priceMap = {};
        try {
          const batchRes = await axios.post(`${API}/market/quotes/batch`, { symbols }, config);
          priceMap = batchRes.data.quotes || {};
          console.log(' Batch prices fetched:', Object.keys(priceMap).length, 'symbols');
        } catch (batchErr) {
          console.warn(' Batch API failed, using existing prices:', batchErr.message);
        }
        
        // Fiyatları ve kategori bilgisini pozisyonlara uygula
        const positionsWithPrices = positionsData.map(pos => {
          const symbol = pos.asset_symbol || pos.symbol;
          const priceData = priceMap[symbol];
          const assetInfo = assetMap[symbol] || {};
          
          // Pozisyondaki kategoriyi kullan, yoksa asset'ten al
          const category = pos.asset_category || assetInfo.category || '';
          const currency = assetInfo.currency || (USD_CATEGORIES.includes(category) ? 'USD' : 'TRY');
          
          let updatedPos = { 
            ...pos, 
            asset_category: category,
            currency: currency
          };
          
          if (priceData) {
            updatedPos.current_price = priceData.price || priceData.current_price;
            updatedPos.live_price = priceData.price || priceData.current_price;
          }
          
          return updatedPos;
        });
        
        console.log('📊 Positions with live prices:', positionsWithPrices.length);
        setPositions(positionsWithPrices);
      }

    } catch (err) {
      console.error('Profile fetch error:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      setUploadMessage('Lütfen bir resim dosyası seçin');
      return;
    }

    // Check file size (max 2MB for images)
    if (file.size > 2 * 1024 * 1024) {
      setUploadMessage('Resim boyutu 2MB\'dan küçük olmalıdır');
      return;
    }

    setUploading(true);
    setUploadMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/profile/upload-image`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadMessage('Profil resmi başarıyla yüklendi!');
      // Refresh user data to get new profile image
      fetchUserProfile();
      
      // Clear message after 3 seconds
      setTimeout(() => setUploadMessage(''), 3000);
    } catch (err) {
      console.error('Upload error:', err);
      setUploadMessage('Yükleme başarısız: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Dosya boyutu 5MB\'dan küçük olmalıdır');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('Belge başarıyla yüklendi! Onay için lütfen bekleyin.');
      fetchUserProfile();
    } catch (err) {
      console.error('Upload error:', err);
      alert('Belge yüklenemedi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center">
        <div className="inline-flex items-center gap-3 text-blue-400">
          <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-xl">Profil yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Kullanıcı bilgisi bulunamadı</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-white hover:text-gray-200 flex items-center gap-2"
            >
              <ArrowLeft className="w-6 h-6" />
              <span>Geri</span>
            </button>
            <h1 className="text-2xl font-bold">Profilim</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-4 space-y-6">
        {/* User Card */}
        <div className="bg-gray-800 rounded-lg p-6 shadow-xl">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative group">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-3xl font-bold overflow-hidden">
                {user.profile_image ? (
                  <img src={user.profile_image} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  user.full_name?.charAt(0) || user.email?.charAt(0).toUpperCase()
                )}
              </div>
              {/* Profile Image Upload Button */}
              <label 
                className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 rounded-full p-1.5 cursor-pointer transition-colors shadow-lg"
                title="Profil resmini değiştir"
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfileImageUpload}
                  className="hidden"
                  disabled={uploading}
                />
                {uploading ? (
                  <svg className="w-4 h-4 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </label>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-1">{user.full_name || 'Kullanıcı'}</h2>
              <p className="text-gray-400">{user.email}</p>
              {uploadMessage && (
                <p className={`text-sm mt-1 ${uploadMessage.includes('başarıyla') ? 'text-green-400' : 'text-red-400'}`}>
                  {uploadMessage}
                </p>
              )}
            </div>
          </div>

          {/* Desktop Layout - User Info */}
          <div className="hidden sm:block">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-gray-700 rounded-lg px-4 py-2">
                  <Hash className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-400 text-sm">Kullanıcı ID:</span>
                  <span className="text-white font-mono font-semibold">{user.user_id || user.id}</span>
                </div>
                <div className="flex items-center gap-3 bg-gray-700 rounded-lg px-4 py-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-400 text-sm">Email:</span>
                  <span className="text-white font-semibold">{user.email}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-gray-700 rounded-lg px-4 py-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-400 text-sm">Hesap Tipi:</span>
                  <span className="text-white font-semibold">{user.account_type || 'STANDART'}</span>
                </div>
                <div className="flex items-center gap-3 bg-gray-600 rounded-lg px-4 py-2">
                  <Wallet className="w-4 h-4 text-green-400" />
                  <span className="text-gray-400 text-sm">Bakiye:</span>
                  <span className="text-green-400 font-bold text-lg">{formatCurrency(user.balance, user)}</span>
                </div>
                {user.credit > 0 && (
                  <div className="flex items-center gap-3 bg-purple-600/30 border border-purple-500 rounded-lg px-4 py-2">
                    <CreditCard className="w-4 h-4 text-purple-300" />
                    <span className="text-purple-300 text-sm">Kredi:</span>
                    <span className="text-purple-300 font-bold text-lg">{formatCurrency(user.credit, user)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Layout - User Info */}
          <div className="sm:hidden">
            <h2 className="text-2xl font-bold mb-3 text-center">{user.full_name || 'Kullanıcı'}</h2>
            <p className="text-gray-400 mb-4 text-center">{user.email}</p>
            
            <div className="space-y-2">
              <div className="flex items-center gap-3 bg-gray-600 rounded-lg px-4 py-2">
                <Hash className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400 text-sm">Kullanıcı ID:</span>
                <span className="text-white font-mono font-semibold">{user.user_id || user.id}</span>
              </div>
              <div className="flex items-center gap-3 bg-gray-600 rounded-lg px-4 py-2">
                <Wallet className="w-4 h-4 text-green-400" />
                <span className="text-gray-400 text-sm">Bakiye:</span>
                <span className="text-green-400 font-bold text-lg">{formatCurrency(user.balance, user)}</span>
              </div>
              <div className="flex items-center gap-3 bg-purple-600/30 border border-purple-500 rounded-lg px-4 py-2">
                <CreditCard className="w-4 h-4 text-purple-300" />
                <span className="text-purple-300 text-sm">Kredi:</span>
                <span className="text-purple-300 font-bold text-lg">{formatCurrency(user.credit || 0, user)}</span>
              </div>
              <div className="flex items-center gap-3 bg-gray-700 rounded-lg px-4 py-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400 text-sm">Hesap Tipi:</span>
                <span className="text-white font-semibold">{user.account_type || 'STANDART'}</span>
              </div>
              <InfoBox label="Kayıt Tarihi" value={new Date(user.created_at).toLocaleDateString('tr-TR')} />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            <button
              onClick={() => navigate('/deposit')}
              className="bg-green-600 hover:bg-green-700 px-4 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            >
              <Banknote className="w-5 h-5" /> Para Yatır
            </button>
            <button
              onClick={() => navigate('/withdraw')}
              className="bg-red-600 hover:bg-red-700 px-4 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            >
              <Building2 className="w-5 h-5" /> Para Çek
            </button>
            <button
              onClick={() => navigate('/documents')}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            >
              <FileText className="w-5 h-5" /> Belge Yükle
            </button>
            <button
              onClick={() => navigate('/support')}
              className="bg-purple-600 hover:bg-purple-700 px-4 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" /> Destek
            </button>
          </div>
        </div>

        {/* Deposit Requests */}
        {depositRequests.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-6 shadow-xl">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-400" /> Para Yatırma Talepleri
            </h3>
            
            {/* Pending Deposits */}
            <div className="mb-6">
              <h4 className="text-md font-semibold mb-2 text-yellow-400 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Bekleyen Talepler
              </h4>
              {depositRequests.filter(d => d.status === 'PENDING').length === 0 ? (
                <div className="bg-gray-700 p-4 rounded-lg text-center text-gray-400">
                  Bekleyen talep yok
                </div>
              ) : (
                <div className="space-y-2">
                  {depositRequests.filter(d => d.status === 'PENDING').map(deposit => (
                    <div key={deposit.id} className="bg-gray-700 p-4 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-yellow-400">{formatCurrency(deposit.amount, user)}</div>
                        <div className="text-xs text-gray-400">{new Date(deposit.created_at).toLocaleString('tr-TR')}</div>
                        <div className="text-xs text-gray-500">{deposit.method}</div>
                      </div>
                      <div className="px-3 py-1 bg-yellow-900/50 text-yellow-300 rounded text-sm">
                        Onay Bekleniyor
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Approved Deposits */}
            <div>
              <h4 className="text-md font-semibold mb-2 text-green-400 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Tamamlananlar
              </h4>
              {depositRequests.filter(d => d.status === 'APPROVED').length === 0 ? (
                <div className="bg-gray-700 p-4 rounded-lg text-center text-gray-400">
                  Onaylanmış talep yok
                </div>
              ) : (
                <div className="space-y-2">
                  {depositRequests.filter(d => d.status === 'APPROVED').map(deposit => (
                    <div key={deposit.id} className="bg-gray-700 p-4 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-green-400">{formatCurrency(deposit.amount, user)}</div>
                        <div className="text-xs text-gray-400">{new Date(deposit.created_at).toLocaleString('tr-TR')}</div>
                        <div className="text-xs text-gray-500">{deposit.method}</div>
                      </div>
                      <div className="px-3 py-1 bg-green-900/50 text-green-300 rounded text-sm flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Onaylandı
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* HESAP DURUMU PANELİ */}
        <div className="bg-gray-800 rounded-xl p-5 shadow-xl">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-400" /> Hesap Durumu
          </h3>

          {/* Sadeleştirilmiş 3'lü ana kutu: Varlık / İşlem Gücü / Bakiye */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {/* Varlık */}
            <div className="bg-green-900/30 rounded-lg p-4 border border-green-700/40" data-testid="profile-box-varlik">
              <div className="text-green-300 text-xs mb-1 font-semibold uppercase tracking-wide">Varlık</div>
              <div className="text-green-400 font-bold text-2xl mb-2" data-testid="profile-varlik">
                ₺{(accountSummary?.varlik ?? ((user?.balance || 0) + openPnL)).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>
              <div className="text-gray-400 text-xs leading-snug">
                Hesabınızın güncel toplam değeri. Bakiyeniz ve açık pozisyonlarınızın anlık kâr/zararı dahildir (kredi hariç).
              </div>
            </div>

            {/* İşlem Gücü */}
            <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700/40" data-testid="profile-box-islem-gucu">
              <div className="text-blue-300 text-xs mb-1 font-semibold uppercase tracking-wide">İşlem Gücü</div>
              <div className="text-blue-400 font-bold text-2xl mb-2" data-testid="profile-islem-gucu">
                ₺{(accountSummary?.islem_gucu ?? tradingStats.freeMargin).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>
              <div className="text-gray-400 text-xs leading-snug">
                Yeni pozisyon açmak için kullanabileceğiniz tutar. Serbest teminatınıza eşittir.
              </div>
            </div>

            {/* Bakiye */}
            <div className="bg-gray-700/60 rounded-lg p-4 border border-gray-600/40" data-testid="profile-box-bakiye">
              <div className="text-gray-300 text-xs mb-1 font-semibold uppercase tracking-wide">Bakiye</div>
              <div className="text-white font-bold text-2xl mb-2" data-testid="profile-bakiye">
                ₺{(user?.balance || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>
              <div className="text-gray-400 text-xs leading-snug">
                Para yatırma/çekme işlemleri için kullanılan nakit bakiyeniz. Açık pozisyon K/Z&apos;si dahil değildir.
              </div>
            </div>
          </div>

          {/* Küçük teminat satırı */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-gray-700/40 rounded-lg px-3 py-2 flex items-center justify-between">
              <span className="text-gray-400 text-xs">Kullanılan Teminat</span>
              <span className="text-orange-400 font-semibold text-sm" data-testid="profile-used-margin">
                ₺{tradingStats.usedMargin.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </span>
            </div>
            <div className="bg-gray-700/40 rounded-lg px-3 py-2 flex items-center justify-between">
              <span className="text-gray-400 text-xs">Serbest Teminat</span>
              <span className="text-cyan-400 font-semibold text-sm" data-testid="profile-free-margin">
                ₺{tradingStats.freeMargin.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </span>
            </div>
          </div>

          {/* Koşullu: Kredi satırı */}
          {user?.credit > 0 && (
            <div className="bg-purple-900/30 border border-purple-700/40 rounded-lg px-3 py-2 mb-3 flex items-center justify-between" data-testid="profile-credit-row">
              <span className="text-purple-300 text-xs flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5" />
                Kredi (çekilemez, sadece işlem için)
              </span>
              <span className="text-purple-300 font-semibold text-sm">
                ₺{(user.credit || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </span>
            </div>
          )}
          
          {/* Margin Seviyesi Progress Bar */}
          <div className="bg-gray-700/50 rounded-lg p-3 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-400 text-xs">Margin Seviyesi</span>
              <span className={`font-bold ${
                tradingStats.usedMargin === 0 ? 'text-gray-400' :
                tradingStats.marginLevel >= 200 ? 'text-green-400' : 
                tradingStats.marginLevel >= 100 ? 'text-yellow-400' : 
                'text-red-400'
              }`}>
                {tradingStats.usedMargin > 0 ? `${tradingStats.marginLevel.toFixed(1)}%` : '-'}
              </span>
            </div>
            <div className="w-full bg-gray-600 rounded-full h-2.5">
              <div 
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  tradingStats.usedMargin === 0 ? 'bg-gray-500' :
                  tradingStats.marginLevel >= 200 ? 'bg-green-500' : 
                  tradingStats.marginLevel >= 100 ? 'bg-yellow-500' : 
                  'bg-red-500'
                }`}
                style={{ width: `${Math.min(tradingStats.usedMargin > 0 ? Math.min(tradingStats.marginLevel, 300) / 3 : 0, 100)}%` }}
              ></div>
            </div>
          </div>
          
          {/* Gerçekleşmemiş K/Z */}
          <div className="bg-gray-700/50 rounded-lg p-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-xs flex items-center gap-1">
                {openPnL >= 0 ? <TrendingUp className="w-4 h-4 text-green-400" /> : <TrendingDown className="w-4 h-4 text-red-400" />}
                Gerçekleşmemiş K/Z
              </span>
              <span className={`font-bold text-lg ${openPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {openPnL >= 0 ? '+' : ''}₺{openPnL.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Margin Call Uyarı Modal */}
      {showMarginCallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-red-900 border-4 border-red-500 rounded-lg p-8 max-w-md w-full text-center animate-pulse">
            <div className="flex justify-center mb-4">
              <AlertTriangle className="w-16 h-16 text-yellow-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">UYARI!</h2>
            <p className="text-xl text-white mb-2">Teminat Seviyesi Kritik!</p>
            <p className="text-2xl font-bold text-yellow-400 mb-4">
              {tradingStats.marginLevel.toFixed(2)}%
            </p>
            <p className="text-white mb-6">
              Bakiyeniz %25&apos;in altına düştü!<br/>
              Lütfen danışmanınıza başvurunuz.
            </p>
            <div className="space-y-2">
              <p className="text-sm text-gray-300">Varlık: ₺{tradingStats.equity.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</p>
              <p className="text-sm text-gray-300">Teminat: ₺{tradingStats.usedMargin.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</p>
            </div>
            <button
              onClick={() => setShowMarginCallModal(false)}
              className="mt-6 bg-white text-red-900 px-6 py-3 rounded-lg font-bold hover:bg-gray-200"
            >
              Anladım
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper Components
function StatBox({ label, value, color = 'text-white' }) {
  return (
    <div className="bg-gray-700 p-3 rounded-lg text-center">
      <div className="text-xs text-gray-400 mb-1">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="flex items-center gap-3 bg-gray-700 rounded-lg px-4 py-2">
      <span className="text-gray-400 text-sm">{label}:</span>
      <span className="text-white font-semibold">{value}</span>
    </div>
  );
}

export default ProfilePage;